import { API } from "../constants/config";

let csrfTokenCache = null;

export async function getCsrfToken(forceRefresh = false) {
  if (csrfTokenCache && !forceRefresh) return csrfTokenCache;
  try {
    const res = await fetch(API + "/security/csrf-token", {
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = await res.json();
    csrfTokenCache = data?.csrfToken || null;
    return csrfTokenCache;
  } catch {
    return null;
  }
}

export async function apiFetch(path, options = {}, meta = {}) {
  const method = (options.method || "GET").toUpperCase();
  const unsafe = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
  const headers = { ...(options.headers || {}) };

  if (meta.authToken) headers.Authorization = `Bearer ${meta.authToken}`;
  if (meta.cartToken) headers["X-Cart-Token"] = meta.cartToken;

  if (unsafe) {
    let csrfToken = null;
    try {
      csrfToken = await getCsrfToken(Boolean(meta.forceCsrfRefresh));
    } catch {
      csrfToken = null;
    }
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  }

  let res;
  try {
    res = await fetch(API + path, {
      ...options,
      method,
      headers,
      credentials: "include",
    });
  } catch {
    return new Response(JSON.stringify({ error: "Network request failed" }), {
      status: 503,
      headers: { "Content-Type": "application/json" },
    });
  }

  if (unsafe && res.status === 403 && !meta.forceCsrfRefresh) {
    const retriedToken = await getCsrfToken(true);
    if (retriedToken) {
      return apiFetch(path, options, { ...meta, forceCsrfRefresh: true });
    }
  }

  const authRefreshExcluded =
    path.startsWith("/auth/login") ||
    path.startsWith("/auth/register") ||
    path.startsWith("/auth/refresh") ||
    path.startsWith("/auth/logout");
  if (res.status === 401 && !authRefreshExcluded && !meta.refreshTried) {
    const refreshRes = await apiFetch(
      "/auth/refresh",
      { method: "POST" },
      { refreshTried: true },
    );
    if (refreshRes.ok) {
      return apiFetch(path, options, { ...meta, refreshTried: true });
    }
  }

  return res;
}

export async function getProjectTelemetry(projectId, { metric, sinceHours } = {}) {
  const params = new URLSearchParams();
  if (metric) params.set("metric", metric);
  if (sinceHours) params.set("since_hours", String(sinceHours));
  const qs = params.toString();
  const res = await apiFetch(`/projects/${projectId}/telemetry${qs ? `?${qs}` : ""}`);
  if (!res.ok) return null;
  return res.json();
}

export function apiErrorMessage(data, fallback) {
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  if (data.error) return data.error;
  if (data.errors?.[0]?.msg) return data.errors[0].msg;
  return fallback;
}

export async function readApiBody(res) {
  const contentType = res.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return res.json().catch(() => ({}));
  }

  const text = await res.text().catch(() => "");
  return text ? { error: text } : {};
}
